/**
 * NativeSelect
 *
 * Drop-in replacement for <select> with a small, fixed set of options.
 *
 * On Capacitor native Android the OS select spinner renders poorly inside
 * WebViews.  On native this component renders inline pill buttons instead
 * (no native dialog is triggered).  On web it renders a standard <select>.
 *
 * Usage:
 *   <NativeSelect
 *     value={status}
 *     onChange={(v) => setStatus(v)}
 *     options={[
 *       { value: 'active',   label: 'Active' },
 *       { value: 'inactive', label: 'Inactive' },
 *     ]}
 *     className="..."   // applied to <select> on web; to the wrapper div on native
 *   />
 */

import { Capacitor } from '@capacitor/core';
import React from 'react';

const IS_NATIVE = Capacitor.isNativePlatform();

export interface NativeSelectOption {
  value: string;
  label: string;
}

interface NativeSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: NativeSelectOption[];
  className?: string;
  id?: string;
  'aria-label'?: string;
  disabled?: boolean;
}

export const NativeSelect: React.FC<NativeSelectProps> = ({
  value,
  onChange,
  options,
  className,
  id,
  'aria-label': ariaLabel,
  disabled,
}) => {
  if (!IS_NATIVE) {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={className}
        id={id}
        aria-label={ariaLabel}
        disabled={disabled}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div
      className="flex flex-wrap gap-1.5"
      id={id}
      aria-label={ariaLabel}
      role="group"
    >
      {options.map((opt) => {
        const isSelected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              isSelected
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 active:bg-gray-100'
            } disabled:opacity-40`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};
